import * as React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import UpdateButton from 'shared/components/UpdateButton';
import { ActionStatus, ProposedAction } from '../types';
import { groupActions } from '../utils';

const STATUS_COLOR: Record<
  ActionStatus,
  'default' | 'info' | 'error' | 'success'
> = {
  proposed: 'default',
  approved: 'info',
  rejected: 'error',
  executed: 'success',
};

interface Props {
  actions: ProposedAction[];
  // Plain-language descriptions of actions that don't line up with the tables
  // (see actionTableMismatches) — rendered as an "investigate" banner.
  warnings?: string[];
  pending: Record<string, 'approve' | 'reject' | undefined>;
  onApprove: (a: ProposedAction) => void;
  onReject: (a: ProposedAction) => void;
}

export default function ProposedActions({
  actions,
  warnings,
  pending,
  onApprove,
  onReject,
}: Props) {
  const groups = groupActions(actions);
  const warningBanner =
    warnings && warnings.length > 0 ? (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          These actions don't match the tables above — investigate before acting:
        </Typography>
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.2 }}>
          {warnings.map((w, i) => (
            <Typography key={i} component="li" variant="body2">
              {w}
            </Typography>
          ))}
        </Box>
      </Alert>
    ) : null;
  if (!groups.length) {
    return (
      <Box>
        {warningBanner}
        <Typography color="text.secondary">No proposed actions.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {warningBanner}
      {groups.map(g => (
        <Box key={g.type} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {g.label} ({g.actions.length})
          </Typography>
          <Stack spacing={1}>
            {g.actions.map(a => {
              const status: ActionStatus = a.status || 'proposed';
              const decided = status !== 'proposed';
              const busy = pending[a.action_id];
              return (
                <Card key={a.action_id} variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {a.ad_name || a.campaign_name}
                        </Typography>
                        {a.campaign_name && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              color: 'text.secondary',
                              display: 'block',
                            }}
                          >
                            {a.campaign_name}
                          </Typography>
                        )}
                        {a.evidence && (
                          <Typography variant="caption" color="text.secondary">
                            {a.evidence}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          label={status}
                          color={STATUS_COLOR[status]}
                        />
                        {!decided && a.type !== 'flag' && (
                          <>
                            <UpdateButton
                              size="small"
                              color="success"
                              variant="contained"
                              loading={busy === 'approve'}
                              disabled={!!busy}
                              onClick={() => onApprove(a)}
                            >
                              Approve
                            </UpdateButton>
                            <UpdateButton
                              size="small"
                              color="error"
                              variant="outlined"
                              loading={busy === 'reject'}
                              disabled={!!busy}
                              onClick={() => onReject(a)}
                            >
                              Reject
                            </UpdateButton>
                          </>
                        )}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}
